/**
 * Shared style utilities for AI summary card components
 *
 * Provides consistent styling functions for PlayerSummaryCard and
 * AIScoutingSummaryCard to avoid code duplication.
 */

import React from "react";
import { Spinner } from "@/components/atoms/Spinner";

import { Text } from "@/components/atoms/Text";
import { Stack } from "@/components/atoms/Stack";

/**
 * Get text classes for card titles
 *
 * Size and weight come from the `title` text-style variant on the consuming
 * `Text` atom — this returns the theme color only.
 * @param isDark - Whether dark mode is active
 * @returns Tailwind class string for title text
 */
export const getTitleTextClasses = (isDark: boolean): string =>
  isDark ? "text-white" : "text-content-primary";

/** Text classes for subtitles and timestamps (the same in both themes). */
export const SUBTITLE_TEXT_CLASS = "text-caption text-content-muted";

/**
 * Get text classes for content body
 * @param isDark - Whether dark mode is active
 * @returns Tailwind class string for content text
 */
export const getContentTextClasses = (isDark: boolean): string =>
  `text-body leading-6 ${isDark ? "text-white" : "text-content-secondary"}`;

/**
 * Get icon color classes
 * @param isDark - Whether dark mode is active
 * @returns Tailwind class string for icon color
 */
export const getIconClasses = (isDark: boolean): string =>
  isDark
    ? "text-needs-semantic-primary-400"
    : "text-needs-semantic-primary-500";

/** Copy icon color class (the same in both themes). */
export const COPY_ICON_CLASS = "text-content-muted";

/**
 * Render loading state content for summary cards
 * @param isDark - Whether dark mode is active
 * @returns JSX element for loading state
 */
export const renderLoadingContent = (isDark: boolean) => (
  <Stack className="items-center justify-center gap-2 py-8">
    <Spinner size="large" color={isDark ? "#60A5FA" : "#2563EB"} />
    <Text
      variant="body"
      className={`${isDark ? "text-white" : "text-content-neutral-strong"}`}
    >
      Generating AI summary...
    </Text>
  </Stack>
);

/**
 * Render error state content for summary cards
 * @param isDark - Whether dark mode is active
 * @param errorMessage - Error message to display
 * @returns JSX element for error state
 */
export const renderErrorContent = (
  isDark: boolean,
  errorMessage: string | null
) => (
  <Stack className="items-center justify-center py-4">
    <Text
      variant="body"
      className={`text-center ${isDark ? "text-needs-semantic-error-400" : "text-status-error"}`}
    >
      {errorMessage || "Failed to generate summary"}
    </Text>
  </Stack>
);

/**
 * Render empty state content for summary cards
 * @returns JSX element for empty state
 */
export const renderEmptyContent = () => (
  <Stack className="items-center justify-center py-8">
    <Text
      variant="unstyled"
      className={`text-center text-body ${SUBTITLE_TEXT_CLASS}`}
    >
      No summary available. Click &quot;Generate&quot; to create one.
    </Text>
  </Stack>
);

/**
 * Render summary text content
 * @param isDark - Whether dark mode is active
 * @param content - Summary content text
 * @returns JSX element with formatted summary text
 */
export const renderSummaryContent = (isDark: boolean, content: string) => (
  <Text variant="unstyled" className={getContentTextClasses(isDark)}>
    {content}
  </Text>
);

/**
 * Get timestamp display text based on generation state
 * @param isGenerating - Whether summary is currently generating
 * @param lastUpdated - Last updated/generated timestamp
 * @returns Formatted timestamp string
 */
export const getTimestampText = (
  isGenerating: boolean,
  lastUpdated: string | null
): string => {
  if (isGenerating) return "Generating summary...";
  if (lastUpdated) return `Last generated: ${lastUpdated}`;
  return "No summary generated yet";
};

/**
 * Get the appropriate content area based on current state
 * @param isDark - Whether dark mode is active
 * @param isGenerating - Whether summary is generating
 * @param hasError - Whether an error occurred
 * @param errorMessage - Error message if error occurred
 * @param summaryContent - Summary content to display
 * @returns JSX element for the current content state
 */
export const getContentArea = (
  isDark: boolean,
  isGenerating: boolean,
  hasError: boolean,
  errorMessage: string | null,
  summaryContent: string
) =>
  isGenerating
    ? renderLoadingContent(isDark)
    : hasError
      ? renderErrorContent(isDark, errorMessage)
      : summaryContent
        ? renderSummaryContent(isDark, summaryContent)
        : renderEmptyContent();
